/**
 * Encrypts any data object to a compact URL-safe string
 * @param {any} data - Any data that can be JSON serialized
 * @param {string} key - Secret key for encryption
 * @returns {string} - Encrypted string (URL-safe)
 */
function encryptData(data: any, key: string): string {
  // Step 1: Convert data to binary format
  const binaryData = serializeData(data);

  // Step 2: Encrypt the binary data
  return encryptBinary(binaryData, key);
}

/**
 * Decrypts data that was encrypted with encryptData
 * @param {string} encryptedString - The encrypted string
 * @param {string} key - Secret key for decryption (same as used for encryption)
 * @returns {any} - The original data object
 */
function decryptData(encryptedString: string, key: string): any {
  // Step 1: Decrypt the binary data
  const binaryData = decryptBinary(encryptedString, key);

  // Step 2: Deserialize back to original data
  return deserializeData(binaryData);
}

/**
 * Serializes data to a compact binary string format
 * @param {any} data - Any data that can be JSON serialized
 * @returns {string} - Binary string representation
 */
function serializeData(data: any): string {
  try {
    // For null or undefined, use a special marker
    if (data === null || data === undefined) {
      return String.fromCharCode(0);
    }

    // Convert to JSON string
    const jsonString = JSON.stringify(data);

    // Convert string length to 4-byte header
    const { length } = jsonString;
    let result = "";
    result += String.fromCharCode((length >> 24) & 0xff);
    result += String.fromCharCode((length >> 16) & 0xff);
    result += String.fromCharCode((length >> 8) & 0xff);
    result += String.fromCharCode(length & 0xff);

    // Add the JSON string
    result += jsonString;

    return result;
  } catch (e) {
    console.error("Serialization failed:", e);
    return String.fromCharCode(0); // Return null marker on error
  }
}

/**
 * Deserializes binary string back to original data
 * @param {string} binaryData - Binary string from serializeData
 * @returns {any} - Original data object
 */
function deserializeData(binaryData: string): any {
  try {
    // Check for null/undefined marker
    if (binaryData.length === 1 && binaryData.charCodeAt(0) === 0) {
      return null;
    }

    // Need at least 4 bytes for the header
    if (binaryData.length < 4) {
      throw new Error("Invalid binary data format");
    }

    // Extract length from 4-byte header
    const length =
      (binaryData.charCodeAt(0) << 24) |
      (binaryData.charCodeAt(1) << 16) |
      (binaryData.charCodeAt(2) << 8) |
      binaryData.charCodeAt(3);

    // Extract JSON string
    const jsonString = binaryData.substring(4, 4 + length);

    // Parse JSON
    return JSON.parse(jsonString);
  } catch (e) {
    console.error("Deserialization failed:", e);
    return null;
  }
}

/**
 * Encrypts a binary string using a simple but effective algorithm
 * @param {string} binaryData - Binary string to encrypt
 * @param {string} key - Secret key
 * @returns {string} - URL-safe encrypted string
 */
function encryptBinary(binaryData: string, key: string): string {
  if (!binaryData) return "";

  // Create key hash
  const keyHash = createKeyHash(key);

  // Encrypt the binary data
  let result = "";
  for (let i = 0; i < binaryData.length; i++) {
    // Generate a unique shift value for each position
    const shift = ((keyHash + i) & 0xff) % 256;

    // Apply the shift
    const charCode = (binaryData.charCodeAt(i) + shift) % 256;
    result += String.fromCharCode(charCode);
  }

  // Base64 encode and make URL-safe
  return btoa(result).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/**
 * Decrypts a string that was encrypted with encryptBinary
 * @param {string} encryptedString - Encrypted URL-safe string
 * @param {string} key - Secret key
 * @returns {string} - Original binary data
 */
function decryptBinary(encryptedString: string, key: string): string {
  if (!encryptedString) return "";

  // Restore standard Base64 characters
  let base64 = encryptedString.replace(/-/g, "+").replace(/_/g, "/");

  // Add padding if needed
  while (base64.length % 4) {
    base64 += "=";
  }

  try {
    // Create key hash
    const keyHash = createKeyHash(key);

    // Decode Base64
    const encrypted = atob(base64);

    // Decrypt the data
    let result = "";
    for (let i = 0; i < encrypted.length; i++) {
      // Generate the same shift value as used in encryption
      const shift = ((keyHash + i) & 0xff) % 256;

      // Reverse the shift
      const charCode = (encrypted.charCodeAt(i) - shift + 256) % 256;
      result += String.fromCharCode(charCode);
    }

    return result;
  } catch (e) {
    console.error("Decryption failed:", e);
    return "";
  }
}

/**
 * Creates a numeric hash from a string key
 * @param {string} key - Secret key
 * @returns {number} - Numeric hash
 */
function createKeyHash(key: string): number {
  let hash = 0;
  for (let i = 0; i < key.length; i++) {
    hash = (hash << 5) - hash + key.charCodeAt(i);
    hash &= hash; // Convert to 32bit integer
  }
  return hash;
}

/**
 * Encrypts data for URL parameter
 * @param {any} data - Any data that can be serialized
 * @param {string} key - Secret key
 * @param {string} [paramName='d'] - URL parameter name
 * @returns {string} - URL parameter string
 */
export function encryptDataString(data: any, key: string, paramName = "d"): string {
  const encrypted = encryptData(data, key);
  return `${paramName}=${encodeURIComponent(encrypted)}`;
}

/**
 * Decrypts data from URL parameter
 * @param {string} urlParam - URL parameter string
 * @param {string} key - Secret key
 * @param {string} [paramName='d'] - URL parameter name
 * @returns {any} - Original data
 */
export function decryptFromUrl(urlParam: string, key: string, paramName = "d"): any {
  const params = new URLSearchParams(urlParam);
  const encrypted = params.get(paramName);

  if (!encrypted) return null;

  return decryptData(encrypted, key);
}
